import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { UserDetails, UserSummary } from '@/types/userDetails';
import { Company } from '@/types/company';
import { UserType } from '@/types/user';

// API response interface
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// Request interfaces for user creation
export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  personalNumber?: string;
  role: 'individual' | 'dealer' | 'business' | 'admin';
  password: string;
  companyId?: string;
  permissions?: string[];
}

export interface CreateAdminUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  personalNumber?: string;
  password: string;
  permissions: string[];
}

export class UserManagementService {
  // Get all users with minimal details (for overview/list views)
  static async getAllUsers(
    page: number = 1, 
    limit: number = 15, 
    searchQuery?: string, 
    userType?: string,
    includeDeleted?: boolean,
    excludeAdmin?: boolean
  ): Promise<{
    users: UserSummary[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }> {
    // If userType is 'admin', use the admin-specific endpoint
    if (userType === 'admin') {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await api.get<{
        success: boolean;
        admins: UserSummary[];
        totalCount: number;
        totalPages: number;
        currentPage: number;
      }>(`/admin/admins?${params.toString()}`);
      
      return {
        users: response.admins || [],
        totalCount: response.totalCount || 0,
        totalPages: response.totalPages || 0,
        currentPage: response.currentPage || page,
      };
    }

    // Build query parameters for regular users
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    
    // Add search parameter if provided
    if (searchQuery && searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    
    // Add userType parameter if provided (only for non-admin types)
    if (userType && userType !== 'all' && userType !== 'admin') {
      params.append('userType', userType);
    }
    
    // Add includeDeleted parameter if provided
    if (includeDeleted === true) {
      params.append('includeDeleted', 'true');
    }
    
    // Add excludeAdmin parameter if provided
    if (excludeAdmin === true) {
      params.append('excludeAdmin', 'true');
    }

    const response = await api.get<{
      success: boolean;
      users: UserSummary[];
      totalCount: number;
      totalPages: number;
      currentPage: number;
    }>(`/admin/users?${params.toString()}`);
    
    return {
      users: response.users || [],
      totalCount: response.totalCount || 0,
      totalPages: response.totalPages || 0,
      currentPage: response.currentPage || page,
    };
  }

  // Get delivery addresses for a specific user (admin only)
  static async getUserDeliveryAddresses(userId: string): Promise<any[]> {
    const response = await api.get<{ success: boolean; data: any[] }>(`/admin/users/${userId}/settings/delivery`);
    return response.data;
  }

  // Admin: create/update/delete delivery addresses for a user
  static async adminCreateDeliveryAddress(userId: string, data: { street: string; postalCode: string; city: string; country?: string; isDefault?: boolean }): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/delivery`, data);
    return response.data;
  }

  static async adminUpdateDeliveryAddress(userId: string, id: string, data: { street?: string; postalCode?: string; city?: string; country?: string }): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/delivery/${id}`, data);
    return response.data;
  }

  static async adminSetDefaultDeliveryAddress(userId: string, id: string): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/delivery/${id}/default`, {});
    return response.data;
  }

  static async adminDeleteDeliveryAddress(userId: string, id: string): Promise<void> {
    await api.delete(`/admin/users/${userId}/settings/delivery/${id}`);
  }

  static async sendDeliveryAddressReminderEmail(userId: string): Promise<void> {
    await api.post<{ success: boolean }>(
      `/admin/users/${userId}/settings/delivery/reminder-email`,
      {},
    );
  }

  // Get payment information for a specific user (admin only)
  static async getUserPaymentInformation(userId: string): Promise<any[]> {
    const response = await api.get<{ success: boolean; data: any[] }>(`/admin/users/${userId}/settings/payment`);
    return response.data;
  }

  // Admin: create/update/delete payment information for a user
  static async adminCreatePaymentInformation(userId: string, data: { bankName: string; accountNumber: string; clearingNumber?: string; accountHolder?: string; isDefault?: boolean }): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/payment`, data);
    return response.data;
  }

  static async adminUpdatePaymentInformation(userId: string, id: string, data: { bankName?: string; accountNumber?: string; clearingNumber?: string; accountHolder?: string }): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/payment/${id}`, data);
    return response.data;
  }

  static async adminSetDefaultPaymentInformation(userId: string, id: string): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/payment/${id}/default`, {});
    return response.data;
  }

  static async adminDeletePaymentInformation(userId: string, id: string): Promise<void> {
    await api.delete(`/admin/users/${userId}/settings/payment/${id}`);
  }

  // Get invoice information for a specific user (admin only)
  static async getUserInvoiceInformation(userId: string): Promise<any[]> {
    const response = await api.get<{ success: boolean; data: any[] }>(`/admin/users/${userId}/settings/invoice`);
    return response.data;
  }

  // Admin: create/update/delete invoice information for a user
  static async adminCreateInvoiceInformation(userId: string, data: { email?: string; phoneNumber?: string; street?: string; postalCode?: string; city?: string; country?: string; useCompanyInfo?: boolean; isDefault?: boolean }): Promise<any> {
    const response = await api.post<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/invoice`, data);
    return response.data;
  }

  static async adminUpdateInvoiceInformation(userId: string, id: string, data: { email?: string; phoneNumber?: string; street?: string; postalCode?: string; city?: string; country?: string }): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/invoice/${id}`, data);
    return response.data;
  }

  static async adminSetDefaultInvoiceInformation(userId: string, id: string): Promise<any> {
    const response = await api.put<{ success: boolean; data: any }>(`/admin/users/${userId}/settings/invoice/${id}/default`, {});
    return response.data;
  }

  static async adminDeleteInvoiceInformation(userId: string, id: string): Promise<void> {
    await api.delete(`/admin/users/${userId}/settings/invoice/${id}`);
  }

  // Get user details by ID (for detailed views)
  static async getUserById(userId: string | number, role?: string): Promise<UserDetails> {
    const userIdString = String(userId);
    
    // If role is 'admin', use the admin-specific endpoint
    if (role === 'admin') {
      const response = await api.get<{
        success: boolean;
        data: UserDetails;
      }>(`/admin/admins/${userIdString}`);
      
      if (!response.success || !response.data) {
        throw new Error('Admin not found');
      }
      
      return response.data;
    }
    
    // Otherwise use the regular user endpoint
    const response = await api.get<{
      success: boolean;
      data: UserDetails;
    }>(`/admin/users/${userIdString}`);
    
    if (!response.success || !response.data) {
      throw new Error('User not found');
    }
    
    return response.data;
  }

  // Update user information (admin functionality)
  static async updateUserInfo(userId: string, userData: Partial<UserDetails>): Promise<UserDetails> {
    // Map frontend UserDetails to backend update format
    const updatePayload: any = {};
    if (userData.username !== undefined) updatePayload.username = userData.username;
    if (userData.email !== undefined) updatePayload.email = userData.email;
    if (userData.phoneNumber !== undefined) updatePayload.phoneNumber = userData.phoneNumber;
    if (userData.personalNumber !== undefined) updatePayload.personalNumber = userData.personalNumber;
    if (userData.address?.zipCode !== undefined) updatePayload.zipCode = userData.address.zipCode;

    const response = await api.put<ApiResponse<UserDetails>>(`/admin/users/${userId}`, updatePayload);
    return response.data; // Extract the data property
  }

  // Update user company (admin functionality)
  static async updateUserCompany(userId: string, companyId: string | null): Promise<UserDetails> {
    const response = await api.put<ApiResponse<UserDetails>>(`/admin/users/${userId}/company`, { companyId });
    return response.data;
  }

  // Get current logged-in admin user's profile (to check permissions)
  static async getCurrentAdminProfile(): Promise<UserDetails> {
    const response = await api.get<{
      success: boolean;
      data: UserDetails;
    }>(`/admin/profile`);
    return response.data;
  }

  // Update admin permissions (admin functionality)
  static async updateAdminPermissions(
    adminId: string,
    permissions: {
      manageUsers?: boolean;
      manageVehicles?: boolean;
      manageSupport?: boolean;
      manageSettings?: boolean;
      manageAdminPermissions?: boolean;
      viewBidsOverview?: boolean;
      viewDashboardOverview?: boolean;
      viewLeadboxListings?: boolean;
    },
    notes?: string
  ): Promise<void> {
    const response = await api.put<{
      success: boolean;
      data: UserDetails;
    }>(`/admin/permissions/${adminId}`, {
      permissions,
      notes,
    });

    if (!response.success) {
      throw new Error("Failed to update admin permissions");
    }
  }

  // Delete user (admin functionality)
  static async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  }

  static async deleteAdmin(adminId: string): Promise<void> {
    const response = await api.delete<{ success: boolean }>(
      `/admin/admins/${adminId}`,
    );
    if (response && response.success === false) {
      throw new Error("Failed to delete admin");
    }
  }

  static async exportUsersCsv(body: {
    roles: Array<"INDIVIDUAL" | "DEALER" | "BUSINESS">;
    newsOptInOnly?: boolean;
    marketingOptInOnly?: boolean;
  }): Promise<{ csv: string; filename: string; count: number }> {
    const response = await api.post<{
      success: boolean;
      data: { csv: string; filename: string; count: number };
    }>("/admin/users/export-csv", body);
    return response.data;
  }

  // Create user (admin functionality)
  static async createUser(userData: CreateUserRequest): Promise<UserDetails> {
    // Map frontend role to backend role
    const roleMap: Record<string, string> = {
      'individual': 'INDIVIDUAL',
      'dealer': 'DEALER',
      'business': 'BUSINESS',
    };
    const backendRole = roleMap[userData.role] || 'INDIVIDUAL';

    const requestPayload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber || null,
      personalNumber: userData.personalNumber || null,
      password: userData.password,
      role: backendRole,
      ...(userData.companyId && { companyId: userData.companyId }),
    };

    const response = await api.post<ApiResponse<UserDetails>>('/admin/users', requestPayload);
    return response.data;
  }

  // Create admin user (admin functionality)
  static async createAdminUser(userData: CreateAdminUserRequest): Promise<UserDetails> {
    // Map permissions array to permissions object
    const permissionsMap: Record<string, boolean> = {};
    if (userData.permissions && Array.isArray(userData.permissions)) {
      userData.permissions.forEach(perm => {
        // Map frontend permission IDs to backend permission keys
        const permissionMap: Record<string, string> = {
          'manage_users': 'manageUsers',
          'support': 'manageUsers', // Support maps to manageUsers
          'manage_vehicles': 'manageVehicles',
          'manage_support': 'manageSupport',
          'manage_news': 'manageSettings', // News management maps to manageSettings
          'manage_coupons': 'manageSettings', // Coupons maps to manageSettings
          'manage_admin_permissions': 'manageAdminPermissions',
          'view_bids_overview': 'viewBidsOverview',
          'view_dashboard_overview': 'viewDashboardOverview',
          'view_leadbox_listings': 'viewLeadboxListings',
        };
        const backendKey = permissionMap[perm] || perm;
        permissionsMap[backendKey] = true;
      });
    }

    // Build username from firstName and lastName
    const username = `${userData.firstName} ${userData.lastName}`.trim();

    const requestPayload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber || '',
      personalNumber: userData.personalNumber || null,
      password: userData.password,
      username,
      permissions: permissionsMap,
    };

    const response = await api.post<ApiResponse<UserDetails>>('/admin/admins', requestPayload);
    return response.data;
  }
}